import { FC, useState } from "react";
import { TextField, Button, IconButton, CircularProgress } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { updateClipShareAction } from "@/api/actions/clipShares";
import { useSnackAlert } from "../../useSnackAlert";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const EditConfirm: FC<{
  clipShareId: number;
  bookmarkId: number;
  currentTitle: string;
  currentDescription?: string;
  handleClose: () => void;
}> = ({ clipShareId, bookmarkId, currentTitle, currentDescription, handleClose }) => {
  const { showSnackbar } = useSnackAlert();
  const [title, setTitle] = useState(currentTitle);
  const [description, setDescription] = useState(currentDescription || "");
  const queryClient = useQueryClient();
  const { mutateAsync: updateClipShare, isPending: isUpdating } = useMutation({
    mutationFn: async () => {
      await updateClipShareAction(clipShareId, {
        title,
        description,
        bookmarkId,
        id: clipShareId,
      });
    },
    onSuccess: () => {
      showSnackbar("Clip share updated successfully", "success");
      queryClient.invalidateQueries({queryKey: ["clipShares"]});
      handleClose();
    },
    onError: () => {
      showSnackbar("Error updating clip share", "error");
    },
  });

  const handleSave = async () => {
    await updateClipShare();
  };

  return (
    <div className={`px-4 py-3 min-w-[320px] rounded-lg transition-all duration-300 ${
      isUpdating ? 'bg-gray-300' : ''
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-base font-semibold text-brand-1 flex-1">
          Edit Clip
        </div>
        <IconButton
          size="small"
          onClick={handleClose}
          className="ml-2 text-neutral-700 hover:bg-neutral-100"
          disabled={isUpdating}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      
      <div className="space-y-3 mb-4">
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          size="small"
          variant="outlined"
          disabled={isUpdating}
        />
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          variant="outlined"
          multiline
          rows={3}
          disabled={isUpdating}
        />
      </div>

      {isUpdating && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#7fa4ca] text-white rounded-lg animate-pulse">
          <CircularProgress size={18} sx={{ color: 'white' }} />
          <span className="text-sm font-medium">Saving your changes...</span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">

        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSave}
          startIcon={!isUpdating ? <EditIcon /> : null}
          className="text-[0.813rem] px-2"
          disabled={isUpdating}
        >
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};